import React, { useState, useEffect } from 'react';
import { Flower2, ChevronLeft, Bell, Smartphone, CheckCircle, XCircle, AlertTriangle, Send, RefreshCw, Wifi, Server } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { notificationsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const PushNotificationTest = () => {
  const navigate = useNavigate();
  const [pushStatus, setPushStatus] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, diagRes] = await Promise.all([
        notificationsAPI.getPushStatus(),
        notificationsAPI.getDiagnostics()
      ]);
      setPushStatus(statusRes.data);
      setDiagnostics(diagRes.data);
    } catch (error) {
      console.error('Error fetching push status:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le statut des notifications',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleTestPush = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const response = await notificationsAPI.testPush();
      setTestResult({
        success: true,
        message: response.data.message,
        details: response.data.result
      });
      toast({
        title: 'Succès',
        description: 'Notification de test envoyée!'
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de l\'envoi',
        error: true
      });
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible d\'envoyer la notification',
        variant: 'destructive'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const StatusIcon = ({ status }) => {
    if (status === true) return <CheckCircle className="w-5 h-5 text-green-400" />;
    if (status === false) return <XCircle className="w-5 h-5 text-red-400" />;
    return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6 text-pink-300" />
              <h1 className="text-xl font-bold text-white">Test Notifications Push</h1>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={fetchStatus}
            className="text-white hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Firebase Status */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Server className="w-5 h-5 text-pink-400" />
            État Firebase
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-pink-200">Firebase initialisé</span>
              <StatusIcon status={diagnostics?.firebase_status?.initialized} />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-pink-200">Configuration complète</span>
              <StatusIcon status={diagnostics?.firebase_status?.configured} />
            </div>
            {diagnostics?.firebase_status?.config_details && (
              <div className="p-3 bg-white/5 rounded-lg space-y-2">
                <p className="text-pink-200 text-sm font-medium">Détails de configuration:</p>
                {Object.entries(diagnostics.firebase_status.config_details).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-pink-200/70">{key}</span>
                    <StatusIcon status={value} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Device Status */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-pink-400" />
            Appareils enregistrés
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-pink-200">Push activé</span>
              <StatusIcon status={pushStatus?.pushEnabled} />
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-pink-200">Nombre d'appareils</span>
              <span className="text-white font-bold">{diagnostics?.user_devices?.count || 0}</span>
            </div>
            {diagnostics?.user_devices?.platforms?.length > 0 && (
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-pink-200 text-sm font-medium mb-2">Plateformes:</p>
                <div className="flex gap-2 flex-wrap">
                  {diagnostics.user_devices.platforms.map((platform, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-pink-500/30 rounded text-xs text-white"
                    >
                      {platform || 'unknown'}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {diagnostics?.user_devices?.count === 0 && (
              <div className="p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                <p className="text-yellow-200 text-sm">
                  Aucun appareil n'est enregistré pour recevoir les notifications push.
                  Pour les activer, vous devez utiliser l'application mobile.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Global Stats */}
        {diagnostics?.global_stats && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Wifi className="w-5 h-5 text-pink-400" />
              Statistiques globales
            </h3>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-pink-300">
                {diagnostics.global_stats.total_active_devices}
              </p>
              <p className="text-pink-200/70 text-sm mt-1">appareils actifs au total</p>
            </div>
          </Card>
        )}

        {/* Test Button */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Send className="w-5 h-5 text-pink-400" />
            Envoyer une notification test
          </h3>
          <p className="text-pink-200 text-sm mb-4">
            Envoyez une notification push à vos appareils enregistrés pour vérifier que tout fonctionne.
          </p>
          <Button 
            onClick={handleTestPush}
            disabled={testLoading || !pushStatus?.pushEnabled}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
          >
            {testLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer notification test
              </>
            )}
          </Button>
          {!pushStatus?.pushEnabled && (
            <p className="text-yellow-300 text-xs mt-2 text-center">
              Aucun appareil enregistré - utilisez l'app mobile pour activer les push
            </p>
          )}
        </Card>

        {/* Test Result */}
        {testResult && (
          <Card className={`p-4 ${testResult.success ? 'bg-green-500/20 border-green-400/30' : 'bg-red-500/20 border-red-400/30'}`}>
            <div className="flex items-start gap-3">
              {testResult.success ? (
                <CheckCircle className="w-6 h-6 text-green-400 shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-red-400 shrink-0" />
              )}
              <div>
                <h4 className={`font-bold ${testResult.success ? 'text-green-300' : 'text-red-300'}`}>
                  {testResult.success ? 'Test réussi!' : 'Test échoué'}
                </h4>
                <p className={`text-sm mt-1 ${testResult.success ? 'text-green-200' : 'text-red-200'}`}>
                  {testResult.message}
                </p>
                {testResult.details && (
                  <pre className="mt-2 text-xs text-white/70 bg-black/20 p-2 rounded overflow-x-auto">
                    {JSON.stringify(testResult.details, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Instructions */}
        <Card className="p-4 bg-blue-500/20 border-blue-400/30">
          <h4 className="text-white font-medium text-sm mb-2">Comment activer les notifications push ?</h4>
          <ol className="text-blue-200 text-xs space-y-2">
            <li>1. Téléchargez l'application mobile (APK Android)</li>
            <li>2. Ouvrez l'application et connectez-vous</li>
            <li>3. Acceptez les permissions de notification</li>
            <li>4. Votre appareil sera automatiquement enregistré</li>
            <li>5. Revenez ici pour tester les notifications</li>
          </ol>
        </Card>
      </div>

      <BottomNav active="notifications" />
    </div>
  );
};

export default PushNotificationTest;
